/** CU CINE E ÎMPĂRȚIT UN MODUL.
 *
 *  Colecția e a omului (migrarea 0045), deci un modul se dă mai departe ca un
 *  proiect: unui OM anume, sau ORGANIZAȚIEI întregi. Două trepte, nu trei —
 *  `viewer` îl vede și generează cu el, `editor` îl și scrie. Nu există
 *  `admin`: cine împarte e proprietarul, și numai el (v.
 *  `services/acces_tronson.py`).
 *
 *  TOATĂ LISTA SE RECITEȘTE DUPĂ FIECARE SCHIMBARE, ca la accesul
 *  proiectului: un grant schimbat poate muta și alte rânduri, iar un răspuns
 *  care ar întoarce doar rândul atins ar cere oricum o a doua cerere.
 */
import { useCallback, useEffect, useState } from "react";
import * as api from "../../api";
import { useSession } from "../../session";
import { useT } from "../../i18n";

export default function ImparteModul({ cod, laInchidere }: {
  cod: string;
  laInchidere: () => void;
}) {
  const t = useT();
  const { user } = useSession();
  const org = user?.organizations?.[0];
  const [granturi, setGranturi] = useState<api.GrantTronson[]>([]);
  const [potImparti, setPotImparti] = useState(false);
  const [membri, setMembri] = useState<api.Member[]>([]);
  const [eroare, setEroare] = useState("");
  const [lucrez, setLucrez] = useState(false);

  const citeste = useCallback(async () => {
    try {
      const r = await api.iaAccesulTronsonului(cod);
      setGranturi(r.granturi);
      setPotImparti(r.pot_imparti);
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    }
  }, [cod]);

  useEffect(() => { void citeste(); }, [citeste]);
  useEffect(() => {
    if (!org?.id) return;
    // Lista din care se alege. O eroare aici nu oprește panoul: granturile
    // care EXISTĂ se văd oricum, și se pot scoate.
    api.listMembers(org.id).then(setMembri).catch(() => setMembri([]));
  }, [org?.id]);

  const schimba = async (fn: () => Promise<unknown>) => {
    setEroare("");
    setLucrez(true);
    try {
      await fn();
      await citeste();
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setLucrez(false);
    }
  };

  /** Cine n-are încă un grant — restul e deja în listă, iar a-l oferi din nou
   *  ar arăta ca o a doua invitație, nu ca o schimbare de rol. */
  const fara = membri.filter((m) => !m.disabled && m.user_id !== user?.id
    && !granturi.some((gr) => gr.user_id === m.user_id));
  const areOrg = granturi.some((gr) => gr.fel === "organizatie");

  return (
    <div className="ob-tr-nou-fundal" onClick={laInchidere}>
      <div className="card ob-tr-nou ob-imparte" onClick={(e) => e.stopPropagation()}>
        <h3>{t("imparte.titlu", { cod })}</h3>

        {!potImparti && (
          <p className="note">{t("imparte.doar_proprietarul")}</p>
        )}
        {eroare && <div className="err">{eroare}</div>}

        <ul className="ob-imparte-lista">
          {granturi.length === 0 && (
            <li className="note">{t("imparte.niciunul")}</li>
          )}
          {granturi.map((gr) => (
            <li key={gr.id}>
              <span className="ob-imparte-cine">
                {gr.fel === "organizatie"
                  ? t("imparte.toata_organizatia", { nume: gr.cine ?? "" })
                  : gr.cine}
              </span>
              <select value={gr.role} disabled={!potImparti || lucrez}
                      onChange={(e) => schimba(() => api.daAccesTronson(
                        cod,
                        gr.fel === "organizatie" ? { org_id: gr.org_id! }
                                                 : { user_id: gr.user_id! },
                        e.target.value as "viewer" | "editor"))}>
                <option value="viewer">{t("imparte.vede")}</option>
                <option value="editor">{t("imparte.scrie")}</option>
              </select>
              {potImparti && (
                <button className="ob-imparte-scoate" disabled={lucrez}
                        onClick={() => schimba(() => api.scoateAccesTronson(
                          cod,
                          gr.fel === "organizatie" ? { org_id: gr.org_id! }
                                                   : { user_id: gr.user_id! }))}>
                  {t("imparte.scoate")}
                </button>
              )}
            </li>
          ))}
        </ul>

        {potImparti && (
          <div className="ob-imparte-adauga">
            {/* ORGANIZAȚIA ÎNTREAGĂ, ca un rând aparte: e cea mai deasă
                alegere — «să-l vadă tot biroul» — și n-are ce căuta amestecată
                printre oameni. */}
            {org && !areOrg && (
              <button className="btn" disabled={lucrez}
                      onClick={() => schimba(() => api.daAccesTronson(
                        cod, { org_id: org.id }, "viewer"))}>
                {t("imparte.da_organizatiei", { nume: org.name })}
              </button>
            )}
            <select value="" disabled={lucrez || fara.length === 0}
                    onChange={(e) => e.target.value && schimba(
                      () => api.daAccesTronson(
                        cod, { user_id: e.target.value }, "viewer"))}>
              <option value="">
                {fara.length ? t("imparte.alege_om") : t("imparte.toti_au")}
              </option>
              {fara.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.nume || m.full_name || m.email}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="ob-imparte-jos">
          <button className="btn" onClick={laInchidere}>
            {t("imparte.gata")}
          </button>
        </div>
      </div>
    </div>
  );
}
